import { Outlet, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { getPopupFullscreenEntries } from '../../lib/strapi';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import CookieBanner from '../CookieBanner/CookieBanner';
import ScrollTopButton from '../ScrollTopButton/ScrollTopButton';
import TrackingScripts from '../TrackingScripts/TrackingScripts';
import UtmCapture from '../UtmCapture/UtmCapture';
import WhatsAppButton from '../WhatsAppButton/WhatsAppButton';
import { isHomeSlug, normalizeRouteSlug } from '../../lib/localizedSlugs';
import styles from './Layout.module.css';

const FlowChat = lazy(() => import('../FlowChat/FlowChat'));
const PopupFullscreen = lazy(() => import('../PopupFullscreen/PopupFullscreen'));

const matchesPagePath = (pageSlug, pathname) => {
  const routeSlug = normalizeRouteSlug(pathname);
  const targetSlug = normalizeRouteSlug(pageSlug);

  if (!routeSlug) {
    return isHomeSlug(targetSlug);
  }

  return routeSlug === targetSlug;
};

const resolveActivePopup = (entries, pathname) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;

  const pagePopup = entries.find((entry) => matchesPagePath(entry?.pageSlug, pathname));
  if (pagePopup) return pagePopup;

  return entries.find((entry) => entry?.mostrarEnTodasLasPaginas) || null;
};

const Layout = () => {
  const { error, header, headerHidden, companyInfo, pageOverrides, locale } = useGlobal();
  const location = useLocation();
  const isHome = isHomeSlug(location.pathname);
  const { hideHeader, hideFooter, hideChatbot } = pageOverrides || {};
  const [popupFullscreenEntries, setPopupFullscreenEntries] = useState([]);

  const handleWhatsappContact = (event) => {
    const button = event.target.closest('a, button');
    if (!button || hideChatbot) return;

    const labels = [button.textContent, button.title, button.getAttribute('aria-label')];
    const isScheduleVisit = labels.some((label) =>
      label?.replace(/\s+/g, ' ').trim().toLowerCase() === 'schedule a visit'
    );
    const href = button.getAttribute('href') || '';
    const isWhatsapp = /^(?:https?:)?\/\/(?:wa\.me|(?:api\.|web\.|www\.)?whatsapp\.com)(?:[/?#]|$)/i.test(href)
      || /^whatsapp:/i.test(href);
    if (!isScheduleVisit && !isWhatsapp) return;

    event.preventDefault();
    event.stopPropagation();
    window.dispatchEvent(new Event('whatsapp-form-open'));
  };

  useEffect(() => {
    let cancelled = false;

    const loadPopupEntries = async () => {
      const entries = await getPopupFullscreenEntries(locale);
      if (!cancelled) {
        setPopupFullscreenEntries(entries);
      }
    };

    loadPopupEntries();

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const activePopupFullscreen = useMemo(
    () => resolveActivePopup(popupFullscreenEntries, location.pathname),
    [location.pathname, popupFullscreenEntries]
  );
  const activePopupKey = activePopupFullscreen
    ? `${normalizeRouteSlug(activePopupFullscreen.pageSlug)}:${activePopupFullscreen.id || 'popup'}`
    : 'popup-fullscreen:none';

  if (error) {
    return (
      <div className={`${styles.layout} ${styles['layout--error']}`}>
        <p>Error al cargar los datos: {error}</p>
        <p>Verifica que Strapi esté corriendo en el puerto correcto.</p>
      </div>
    );
  }

  const mainClassName = [
    styles.layout__main,
    header?.isSticky && !hideHeader ? styles['layout__main--with-sticky-header'] : null,
    header?.isSticky && !isHome && !hideHeader ? styles['layout__main--inner-page'] : null,
    header?.isSticky && headerHidden && !hideHeader ? styles['layout__main--header-hidden'] : null,
    hideHeader ? styles['layout__main--no-header'] : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.layout} onClickCapture={handleWhatsappContact}>
      {!hideHeader && <Header />}
      <main className={mainClassName}>
        <Outlet />
      </main>
      {activePopupFullscreen && (
        <Suspense fallback={null}>
          <PopupFullscreen key={activePopupKey} popup={activePopupFullscreen} />
        </Suspense>
      )}
      <CookieBanner />
      <ScrollTopButton />
      {!hideChatbot && <WhatsAppButton />}
      {!hideChatbot && companyInfo?.chatbot?.enabled && companyInfo?.chatbot?.flowId && (
        <Suspense fallback={null}>
          <FlowChat companyInfo={companyInfo} />
        </Suspense>
      )}
      <TrackingScripts trackingIds={companyInfo?.trackingIds} />
      <UtmCapture />
      {!hideFooter && <Footer />}
    </div>
  );
};

export default Layout;
